import apiClient from './apiClient';
import type { SpecialtyResponse } from './specialtyApi';

export type InvitationStatus = 'NOT_INVITED' | 'PENDING' | 'ACCEPTED' | 'EXPIRED';

export interface EmployeeResponse {
    id: string;
    salonId: string;
    firstName: string;
    lastName: string;
    email?: string;
    phone?: string;
    photoUrl?: string;
    role: 'OWNER' | 'MANAGER' | 'STYLIST' | 'RECEPTIONIST';
    specialties: SpecialtyResponse[];
    commissionPercentage: number;
    isActive: boolean;
    invitationStatus: InvitationStatus;
    invitationSentAt?: string;
    hasUserAccount: boolean;
}

export interface EmployeeCreateRequest {
    salonId: string;
    firstName: string;
    lastName: string;
    email?: string;
    phone?: string;
    role: 'OWNER' | 'MANAGER' | 'STYLIST' | 'RECEPTIONIST';
    specialtyIds: string[];
    commissionPercentage: number;
    isActive: boolean;
}

import type { BulkImportResponse } from './clientApi';

export interface EmployeeBulkImportRequest {
    salonId: string;
    employees: {
        fullName: string;
        email?: string;
        phone?: string;
        role: string;
    }[];
}

export interface InvitationResponse {
    employeeId: string;
    email: string;
    status: InvitationStatus;
    sentAt: string;
    message: string;
}

export interface InvitationDetails {
    employeeName: string;
    email: string;
    salonName: string;
    roleName: string;
}

export interface CompleteInvitationRequest {
    token: string;
    employeeId: string;
    password: string;
}

export const employeeApi = {
    getSalonEmployees: async (salonId: string): Promise<EmployeeResponse[]> => {
        const response = await apiClient.get<EmployeeResponse[]>(`/employees/salon/${salonId}`);
        return response.data;
    },

    getEmployeeById: async (id: string): Promise<EmployeeResponse> => {
        const response = await apiClient.get<EmployeeResponse>(`/employees/${id}`);
        return response.data;
    },

    createEmployee: async (data: EmployeeCreateRequest): Promise<EmployeeResponse> => {
        const response = await apiClient.post<EmployeeResponse>('/employees', data);
        return response.data;
    },

    bulkImport: async (data: EmployeeBulkImportRequest): Promise<BulkImportResponse> => {
        const response = await apiClient.post<BulkImportResponse>('/employees/bulk', data);
        return response.data;
    },

    updateEmployee: async (id: string, data: Partial<EmployeeCreateRequest>): Promise<EmployeeResponse> => {
        const response = await apiClient.put<EmployeeResponse>(`/employees/${id}`, data);
        return response.data;
    },

    // ================================
    // Employee Portal - Invitations
    // ================================

    /**
     * Send an invitation to an employee to join the system.
     * Requires authentication (Owner/Manager).
     */
    inviteEmployee: async (employeeId: string): Promise<InvitationResponse> => {
        const response = await apiClient.post<InvitationResponse>(`/employees/${employeeId}/invite`);
        return response.data;
    },

    /**
     * Resend an invitation that was previously sent.
     */
    resendInvitation: async (employeeId: string): Promise<InvitationResponse> => {
        const response = await apiClient.post<InvitationResponse>(`/employees/${employeeId}/resend-invite`);
        return response.data;
    },

    /**
     * Validate an invitation token (public endpoint).
     * Called when employee clicks the link in their email.
     */
    validateInvitation: async (token: string, employeeId: string): Promise<InvitationDetails> => {
        const response = await apiClient.get<InvitationDetails>('/employees/validate-invitation', {
            params: { token, employeeId }
        });
        return response.data;
    },

    /**
     * Complete the invitation by creating the user account (public endpoint).
     */
    completeInvitation: async (data: CompleteInvitationRequest): Promise<unknown> => {
        const response = await apiClient.post('/employees/complete-invitation', data);
        return response.data;
    },
};
